import React, { ReactElement } from 'react'
import { Country } from '@afterpay/types'
import { useFlag } from '@afterpaytouch/feature'
import { TermLink, TermLinkProps } from '../TermLink'
import { useTranslation } from '../../utils/convergence'
import { useApplePaylaterLinkParams, useIsCashApp, useIsCountry, useUkRegFlags } from '../../state'
import { DesignLanguage, useIsDesignLanguage } from '@afterpaytouch/core'
import { Features } from '../../model'

interface UseSignUpTermsResult {
  readonly [tagName: string]: ReactElement<TermLinkProps>
}

export const useTermsKey = (): string => {
  const isCashApp = useIsCashApp()
  const { isUkCreditCheckEnabled, isUkRegulationEnabled } = useUkRegFlags()
  const isAuCountry = useIsCountry(Country.AU)
  const isAuPayIn4UpdatedTerms = useFlag(Features.AU_PAY_IN_4_UPDATED_TERMS)
  if (isUkCreditCheckEnabled) {
    return isUkRegulationEnabled ? 'signUp:terms_creditCheck_ukRegulation' : 'signUp:terms_creditCheck'
  }
  if (isCashApp) {
    return 'signUp:cash_terms'
  }
  if (isAuCountry && isAuPayIn4UpdatedTerms) {
    return 'signUp:terms_auPayIn4'
  }
  return 'signUp:terms'
}

export const useSignUpTerms = (): UseSignUpTermsResult => {
  const { t } = useTranslation()
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)
  const linkColor = isArcadeDesignLanguage ? 'Black' : 'Orca'
  const withApplePaylaterParams = useApplePaylaterLinkParams()

  return {
    cashapplink: (
      <TermLink key='payment-options-cash-app' color={linkColor} href={withApplePaylaterParams(t('common:cashAppPrivacyPolicyUrl'))} fontFace='CashSansMono' />
    ),
    terms: <TermLink key='sign-up-terms-of-service' href={withApplePaylaterParams(t('common:termsOfServiceUrl'))} color={linkColor} fontFace='CashSansMono' />,
    consent: (
      <TermLink
        key='payment-options-consent'
        color={linkColor}
        href={withApplePaylaterParams(`${t('common:termsOfServiceUrl')}#e-sign`)}
        fontFace='CashSansMono'
      />
    ),
    privacy: <TermLink key='sign-up-privacy-policy' href={withApplePaylaterParams(t('common:privacyPolicyUrl'))} color={linkColor} fontFace='CashSansMono' />,
    optOut: <TermLink key='sign-up-opt-out' href={withApplePaylaterParams(t('common:marketingOptOutUrl'))} fontFace='CashSansMono' />,
    creditcheck: <TermLink key='sign-up-credit-check' href={withApplePaylaterParams(t('common:creditCheckUrl'))} color={linkColor} fontFace='CashSansMono' />,
    customerterms: (
      <TermLink key='sign-up-customer-terms' href={withApplePaylaterParams(t('common:customerTermsUrl'))} color={linkColor} fontFace='CashSansMono' />
    ),
    privacypolicy: (
      <TermLink
        key='sign-up-privacy-policy-pay-in-4'
        href={withApplePaylaterParams(t('common:privacyPolicyPayIn4Url'))}
        color={linkColor}
        fontFace='CashSansMono'
      />
    ),
  }
}
